import { useMemo } from 'react';
import * as Sentry from '@sentry/react';
import { sub } from 'date-fns';
import { useSelector } from 'react-redux';

import { Retailer } from 'clipsal-cortex-types/src/api/api-retailer';
import { TariffTemplate } from 'clipsal-cortex-types/src/api/api-tariff-templates';
import { SiteTariff, SiteTariffToSave } from 'clipsal-cortex-types/src/api/api-tariffs-v2';
import { PaginatedResponseV2 } from 'clipsal-cortex-types/src/common/types';
import { getStringifiedQueryParams } from 'clipsal-cortex-utils/src/formatting/query-params';

import { baseApi } from '../../../app/services/baseApi';
import { EMPTY_RETAILER, IS_DEMO_LOGIN } from '../../../common/constants';
import { selectSite } from '../../site/siteSlice';
import { mapAPIValuesToForm } from './form-mapping-utils';
import { TariffFormDataState } from './tariff-form-context';

export type RetailerQueryParams = {
  limit: number;
  offset: number;
  country_code?: string;
  search?: string;
};

export type TariffQueryParams = {
  limit: number;
  offset: number;
};

export const tariffApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getRetailers: build.query<PaginatedResponseV2<Retailer>, RetailerQueryParams | undefined>({
      query: (params) => {
        let uri = `/v1/retailers?country_code=AU`;
        if (params) uri += `&${getStringifiedQueryParams<string>(params)}`;
        return uri;
      },
      providesTags: ['Retailers'],
    }),
    getTariffs: build.query<
      PaginatedResponseV2<Required<SiteTariff>>,
      { siteId: number; params: Partial<TariffQueryParams> }
    >({
      query: ({ siteId, params }) => {
        const stringifiedParams = getStringifiedQueryParams<string>(params);
        return `/v2/sites/${siteId}/tariffs${stringifiedParams ? '?' : ''}${stringifiedParams}`;
      },
      providesTags: ['Tariffs'], // @TODO: separate cache by page
    }),
    getTariffTemplates: build.query<TariffTemplate[], { distributor_id: number; retailer_id: number }>({
      query: (params) => {
        const stringifiedParams = getStringifiedQueryParams<string>(params);
        return `/v1/tariff_templates${stringifiedParams ? '?' : ''}${stringifiedParams}`;
      },
      providesTags: ['TariffTemplates'],
    }),
    getTariffForForm: build.query<TariffFormDataState, number>({
      query: (siteTariffId) => `/v2/site_tariffs/${siteTariffId}`,
      providesTags: ['Tariff'],
      transformResponse: (apiTariff: Required<SiteTariff>) => {
        const transformedTariff = {
          ...apiTariff,
          tariff: {
            ...apiTariff.tariff,
            retailer: apiTariff.tariff.retailer ?? EMPTY_RETAILER,
          },
        };
        try {
          return mapAPIValuesToForm(transformedTariff);
        } catch (e) {
          Sentry.captureException(e, {
            tags: {
              category: 'feature',
              feature: 'bills',
              helper: 'tariffApi',
              method: 'getTariffForForm:transformResponse',
            },
            extra: {
              // NOTE: this must be a string, nested structures don't show in Sentry
              tariffAPIData: JSON.stringify(transformedTariff),
            },
          });

          throw e;
        }
      },
    }),
    createTariff: build.mutation<SiteTariff, { siteId: number; body: SiteTariffToSave }>({
      query: ({ siteId, body }) => ({
        url: `/v2/sites/${siteId}/tariffs`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Tariffs', 'Tariff'],
    }),
    updateTariff: build.mutation<SiteTariff, SiteTariffToSave>({
      query: (body) => ({
        url: `/v2/site_tariffs/${body.id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['Tariffs', 'Tariff'],
    }),
    deleteTariff: build.mutation<SiteTariff, number>({
      query: (tariffId) => ({
        url: `/v2/site_tariffs/${tariffId}`,
        method: 'DELETE',
        body: {},
      }),
      invalidatesTags: ['Tariffs'],
    }),
  }),
});

export const {
  useGetRetailersQuery,
  useGetTariffsQuery,
  useGetTariffTemplatesQuery,
  useGetTariffForFormQuery,
  useUpdateTariffMutation,
  useCreateTariffMutation,
  useDeleteTariffMutation,
} = tariffApi;

export const useRetailers = (params?: RetailerQueryParams) => {
  const queryResult = useGetRetailersQuery(params);
  const { data } = queryResult;

  return {
    ...queryResult,
    retailers: data?.results ?? [],
    count: data?.count ?? 0,
  };
};

export const useTariffs = (params?: Partial<TariffQueryParams>, skip = false) => {
  const site = useSelector(selectSite);
  const queryResult = useGetTariffsQuery({ params: params ?? {}, siteId: site.site_id }, { skip });
  const { data } = queryResult;

  const tariffs = (data?.results ?? []).map(({ tariff, ...other }) => ({
    ...other,
    tariff: {
      ...tariff,
      retailer: tariff.retailer === null ? EMPTY_RETAILER : tariff.retailer,
    },
  }));

  return {
    ...queryResult,
    data: {
      ...queryResult.data,
      results: tariffs,
      count: data?.count ?? 0,
      next: data?.next ?? null,
      previous: data?.previous ?? null,
    },
  };
};

export const useMostRecentTariff = () => {
  const queryResult = useTariffs({ offset: 0, limit: 5 });
  const { data } = queryResult;
  const mostRecentTariff = useMemo(() => data?.results?.[0], [data]);

  const isTariffOlderThanAYear = useMemo(() => {
    if (!mostRecentTariff) return false;
    const oneYearAgo = sub(new Date(), { years: 1 });
    return new Date(mostRecentTariff.tariff_effective_date) < oneYearAgo;
  }, [mostRecentTariff]);

  return {
    ...queryResult,
    data: mostRecentTariff,
    isTariffOlderThanAYear,
    // avoid updates in demo login
    needToUpdateTariff: !IS_DEMO_LOGIN && (isTariffOlderThanAYear || !mostRecentTariff),
  };
};
